import type {
  ComponentPropsWithRef,
  ForwardedRef,
  JSX,
  MouseEvent,
  ReactNode,
  AnchorHTMLAttributes,
} from 'react';
import { forwardRef } from 'react';
import { styled } from 'styled-components';
import { Link as RouterLink, useInRouterContext } from 'react-router';
import { isFunction, isNil, isNotNil, isNotUndefined, isUndefined } from '@wistia/type-guards';
import type { ColorSchemeTypes } from '../ColorSchemeWrapper';
import { getColorScheme } from '../../private/helpers/getColorScheme';
import { buttonResetCss } from '../../css/buttonResetCss';
import { iconInlineStyle } from '../Icon/Icon';

export type LinkTypes = 'default' | 'email' | 'external' | 'phone';

// this is copied from react-router because VMA uses an older version and does not have this type
// some types have been commented out because we don't use them, and they would make the type more complex
// TODO: when VMA is on react-router v7 we can remove this and import from 'react-router'
type ReactRouterLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  // discover?: DiscoverBehavior;
  // prefetch?: PrefetchBehavior;
  reloadDocument?: boolean;
  replace?: boolean;
  state?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  preventScrollReset?: boolean;
  // to: To;
  // relative?: RelativeRoutingType;
  viewTransition?: boolean;
};

const generateHref = (
  href: string | undefined,
  type: LinkTypes | undefined,
  disabled: boolean,
): string | undefined => {
  // when a link is disabled, we don't want an href value because
  // the browser displays a tooltip "hint" of the href to the user
  if (disabled || isNil(href)) {
    return undefined;
  }

  let modifiedHref = href;

  if (type === 'phone') {
    modifiedHref = `tel:${href}`;
  }

  if (type === 'email') {
    modifiedHref = `mailto:${href}`;
  }
  return modifiedHref;
};

type BaseProps = {
  /**
   * A Promise to invoke before navigating to location.
   * _Caution: if used with `external` type prop a new window will **not** be opened_
   */
  beforeAction?: (() => Promise<void>) | (() => void);
  /**
   * Text that appears inside of the link
   *
   * @type ReactNode
   */
  children: ReactNode;
  /**
   * Sets the [color scheme](../?path=/docs/docs-color-schemes--docs)
   */
  colorScheme?: ColorSchemeTypes;
  /**
   * Disables the link
   */
  disabled?: boolean;
  /**
   * When true, the link will inherit its color from the parent element instead of using the default link color
   */
  inheritColor?: boolean;
  /**
   * Icon to display on the left side of the link. Must be an instance of [Icon](?path=/docs/components-icon--docs)
   */
  leftIcon?: ReactNode | undefined;
  /**
   * Icon to display on the right side of the link. Must be an instance of [Icon](?path=/docs/components-icon--docs)
   */
  rightIcon?: ReactNode | undefined;
  /**
   * Type of link to display
   *
   *   * `email` creates a mailto
   *   * `phone` creates a telephone number
   *   * `external` will open links in a new window
   */
  type?: LinkTypes | undefined;
  /**
   * Controls the underline behavior of the link
   *
   * * `none` - no underline (default)
   * * `always` - always show underline
   * * `hover` - show underline only on hover
   */
  underline?: 'always' | 'hover' | 'none';
};

type LinkAsLinkProps = BaseProps &
  ComponentPropsWithRef<'a'> &
  ReactRouterLinkProps & {
    href: string | undefined;
    type?: LinkTypes;
  };

type LinkAsButtonProps = BaseProps &
  ComponentPropsWithRef<'button'> & {
    href?: never;
    type?: 'button' | 'reset' | 'submit';
  };

export type LinkProps = LinkAsButtonProps | LinkAsLinkProps;

const isButton = (props: Omit<LinkProps, 'children'>): props is LinkAsButtonProps =>
  isUndefined(props.href);

const isLink = (props: Omit<LinkProps, 'children'>): props is LinkAsLinkProps =>
  isNotUndefined(props.href);

type StyledLinkProps = {
  href?: string | undefined;
  $colorScheme: ColorSchemeTypes;
  $disabled: boolean;
  $underline: 'always' | 'hover' | 'none';
  $inheritColor: boolean;
  $hasIcons: boolean;
};

const StyledLink = styled.a<StyledLinkProps>`
  ${({ href }) => isNil(href) && buttonResetCss};
  ${({ $colorScheme }) => getColorScheme($colorScheme)}
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  font-family: inherit;
  color: ${({ $disabled, $inheritColor }) => {
    if ($disabled) return 'var(--wui-color-text-disabled)';
    if ($inheritColor) return 'inherit';
    return 'var(--wui-color-text-link)';
  }};
  text-decoration: ${({ $underline }) => ($underline === 'always' ? 'underline' : 'none')};
  display: inline-flex;
  align-items: baseline;
  gap: var(--wui-space-01);
  ${iconInlineStyle}

  &:hover {
    text-decoration: ${({ $underline }) =>
      $underline === 'hover' || $underline === 'always' ? 'underline' : 'none'};
  }

  button& {
    display: inline;
    line-height: inherit;
    vertical-align: baseline;
  }

  && {
    svg {
      width: 14px;
      height: 14px;
      flex: 0 0 14px;
    }
  }
`;

/**
 * Links are used for navigation. If no `href` is supplied, a button that is visually styled to appear as a link
 * is rendered. If used in a `react-router` context, their [Link](https://reactrouter.com/en/main/components/link).
 * component is used, otherwise a native `a` element.
 *
 * This means in addition to its own props, `Link` can use props from `react-router`'s `Link` component. View their documentation [here](https://reactrouter.com/en/main/components/link).
 * The one prop we ignore from react-router is `to`. We use `href` instead and map it under the hood.
 */
export const Link = forwardRef<HTMLAnchorElement | HTMLButtonElement, LinkProps>(
  (
    {
      beforeAction,
      children,
      type,
      disabled = false,
      colorScheme = 'inherit',
      underline = 'none',
      inheritColor = false,
      leftIcon,
      rightIcon,
      'aria-disabled': ariaDisabled,
      role,
      ...props
    }: LinkProps,
    ref,
  ): JSX.Element | null => {
    // see: https://api.reactrouter.com/v7/functions/react_router.useInRouterContext.html
    const inRouterContext = useInRouterContext();
    const to = generateHref(props.href, type, disabled);
    const shouldUseReactRouterLink =
      inRouterContext && type !== 'external' && !to?.startsWith('http');
    const hasIcons = isNotNil(leftIcon) || isNotNil(rightIcon);
    // note: an external type link would require `window.open(this.href,'_blank')` here
    // but that will be swallowed by the browser popup blocker
    // better to just treat an external link with a beforeAction as a default link
    // see: https://www.w3.org/TR/DOM-Level-3-Events/#trusted-events
    const handleClick = async (event: MouseEvent) => {
      if (disabled) {
        event.preventDefault();
      } else if (beforeAction) {
        if (!shouldUseReactRouterLink) {
          event.preventDefault();
        }

        try {
          await beforeAction();
        } catch (error: unknown) {
          console.error(error); // eslint-disable-line no-console
        } finally {
          if (isFunction(props.onClick)) {
            props.onClick(event);
          }

          // Manually load the page if we aren't in a router context
          if (!shouldUseReactRouterLink && isNotNil(to)) {
            window.location.assign(to);
          }
        }
      } else if (isFunction(props.onClick)) {
        props.onClick(event);
      } else {
        // do nothing
      }
    };

    const commonProps = {
      $colorScheme: colorScheme,
      $disabled: disabled,
      $underline: underline,
      $inheritColor: inheritColor,
      $hasIcons: hasIcons,
      onClick: handleClick,
      'aria-disabled': ariaDisabled ?? (disabled ? 'true' : undefined),
    };

    if (isButton(props)) {
      return (
        <StyledLink
          ref={ref as ForwardedRef<HTMLButtonElement>}
          as="button"
          type="button"
          {...props}
          {...commonProps}
        >
          {children}
        </StyledLink>
      );
    }

    if (isLink(props)) {
      const externalLinkProps =
        type === 'external' ? { target: '_blank', rel: 'noopener noreferrer' } : null;

      const routerSpecificProps = shouldUseReactRouterLink
        ? {
            // TODO: Using 'any' here due to styled-components typing limitations with RouterLink
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            as: RouterLink as any,
            to: to ?? '',
          }
        : {
            as: 'a' as const,
            href: to,
          };

      const combinedProps = {
        ref: ref as ForwardedRef<HTMLAnchorElement>,
        ...props,
        ...commonProps,
        ...externalLinkProps,
        ...routerSpecificProps,
      };

      return (
        <StyledLink
          {...combinedProps}
          role={role ?? (disabled ? 'link' : undefined)}
        >
          {leftIcon ?? null}
          {children}
          {rightIcon ?? null}
        </StyledLink>
      );
    }

    // This should never return null, but is necessary because type narrowing has to be used to make refs play nice
    return null;
  },
);

Link.displayName = 'Link';
